import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export interface ProgressRingProps {
  value: number;
  max: number;
  /** Srednica pierscienia w pikselach. */
  size?: number;
  /** Grubosc obreczy w pikselach. */
  thickness?: number;
  /** Tresc w srodku. Domyslnie „wartość / maksimum”. */
  children?: ReactNode;
  /** Podpis pod wartoscia (np. „zawodników”). */
  caption?: string;
  /** Opis dla czytnikow ekranu, gdy srodek nie zawiera tekstu. */
  label?: string;
  className?: string;
}

/**
 * Pierscieniowy wskaznik postepu — odpowiednik „27 / 32 zawodników”
 * z ekranu dodawania graczy.
 *
 * Rysowany w SVG, wiec pozostaje ostry przy kazdym powiekszeniu i na ekranach
 * o wysokiej gestosci pikseli.
 */
export function ProgressRing({
  value,
  max,
  size = 160,
  thickness = 14,
  children,
  caption,
  label,
  className,
}: ProgressRingProps) {
  const safeMax = max > 0 ? max : 1;
  const ratio = Math.min(1, Math.max(0, value / safeMax));

  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  return (
    <div className={cn('relative inline-flex shrink-0 items-center justify-center', className)}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={label ?? `${value} z ${safeMax}`}
        className="-rotate-90"
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--color-surface-sunken)"
          strokeWidth={thickness}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--color-brand)"
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {children ?? (
          <>
            <span className="text-fg-strong text-2xl font-extrabold tracking-[-0.02em]">
              {value} <span className="text-fg-subtle font-bold">/ {safeMax}</span>
            </span>
            {caption && <span className="text-fg-muted text-xs">{caption}</span>}
          </>
        )}
      </div>
    </div>
  );
}
